const ROOT_SELECTOR = "[data-bundle-progress]";
const MESSAGE_SELECTOR = "[data-bundle-progress-message]";
const FILL_SELECTOR = "[data-bundle-progress-fill]";
const TEMPLATES_SELECTOR = "script[data-bundle-progress-templates]";

const widgetMemo = new WeakMap();
const warnedElements = new WeakSet();

function warnOnce(el, message, extra) {
  if (warnedElements.has(el)) return;
  warnedElements.add(el);
  if (extra !== undefined) {
    console.warn(`[bundle-progress] ${message}`, extra);
  } else {
    console.warn(`[bundle-progress] ${message}`);
  }
}

function formatMoney(cents) {
  const safeCents = Number.isFinite(cents) && cents > 0 ? Math.round(cents) : 0;
  const dollars = safeCents / 100;
  return safeCents % 100 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
}


function applyTemplate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (match, key) => (key in vars ? String(vars[key]) : match));
}

function collectBundleStats(cart, tag) {
  let propertyCount = 0;
  let discountCents = 0;
  let listCents = 0;

  for (const item of cart.items) {
    if (!item || !item.properties || item.properties._bundle !== tag) continue;
    propertyCount += Number(item.quantity) || 0;
    discountCents += Number(item.line_level_total_discount) || 0;
    listCents += Number(item.original_line_price) || 0;
  }

  return { propertyCount, discountCents, listCents };
}

function computeSavingsCents(discountCents, listCents, displayCount, qty, priceWholeDollars) {
  if (discountCents > 0) return discountCents;
  if (qty <= 0) return 0;
  const completedSets = Math.floor(displayCount / qty);
  const bundlePriceTotalCents = priceWholeDollars * 100 * completedSets;
  return Math.max(0, listCents - bundlePriceTotalCents);
}

function updateWidget(root, cart) {
  const tag = root.dataset.bundleTag;
  const qty = Number(root.dataset.bundleQty);
  const priceWholeDollars = Number(root.dataset.bundlePrice);

  if (!tag || !Number.isFinite(qty) || qty <= 0 || !Number.isFinite(priceWholeDollars) || priceWholeDollars < 0) {
    warnOnce(root, "invalid data-bundle-* attributes; leaving server-rendered state as-is");
    return;
  }

  const messageEl = root.querySelector(MESSAGE_SELECTOR);
  const fillEl = root.querySelector(FILL_SELECTOR);
  if (!messageEl || !fillEl) {
    warnOnce(root, "message/fill hooks not found; leaving server-rendered state as-is");
    return;
  }

  let memo = widgetMemo.get(root);
  if (memo && memo.invalid) return;

  if (!memo) {
    const templatesEl = root.querySelector(TEMPLATES_SELECTOR);
    if (!templatesEl) {
      warnOnce(root, "no [data-bundle-progress-templates] script found; leaving server-rendered state as-is");
      widgetMemo.set(root, { invalid: true });
      return;
    }

    let templates;
    try {
      templates = JSON.parse(templatesEl.textContent);
    } catch (err) {
      warnOnce(root, "could not parse bundle progress templates JSON; leaving server-rendered state as-is", err);
      widgetMemo.set(root, { invalid: true });
      return;
    }

    if (!templates || typeof templates !== "object") {
      warnOnce(root, "bundle progress templates JSON is not an object; leaving server-rendered state as-is");
      widgetMemo.set(root, { invalid: true });
      return;
    }

   
    const serverInitialCount = Number(root.dataset.bundleCount) || 0;
    const { propertyCount: initialPropertyCount } = collectBundleStats(cart, tag);
    const offset = Math.max(0, serverInitialCount - initialPropertyCount);

    memo = { templates, offset, priceMoney: formatMoney(priceWholeDollars * 100) };
    widgetMemo.set(root, memo);
  }

  const { propertyCount, discountCents, listCents } = collectBundleStats(cart, tag);
  const displayCount = propertyCount + memo.offset;

  let phase = "empty";
  if (displayCount >= qty) phase = "complete";
  else if (displayCount > 0) phase = "partial";

  const templateKey = phase === "partial" ? "partial_pdp" : phase;
  const template = memo.templates[templateKey];
  if (typeof template !== "string") {
    warnOnce(root, `missing "${templateKey}" template; leaving server-rendered state as-is`);
    return;
  }

  let message;
  if (phase === "complete") {
    const savingsCents = computeSavingsCents(discountCents, listCents, displayCount, qty, priceWholeDollars);
    message = applyTemplate(template, { savings: formatMoney(savingsCents) });
  } else if (phase === "partial") {
    const remaining = Math.max(0, qty - displayCount);
    message = applyTemplate(template, { count: remaining, qty, price: memo.priceMoney });
  } else {
    message = applyTemplate(template, { qty, price: memo.priceMoney });
  }

  const progressPct = Math.min(100, Math.max(0, (displayCount * 100) / qty));

  messageEl.textContent = message;
  fillEl.style.width = `${progressPct}%`;
  root.dataset.bundleCount = String(displayCount);
  root.dataset.bundleState = phase;
}

function handleCartUpdate() {
  const cart = window.liquidAjaxCart && window.liquidAjaxCart.cart;
  if (!cart || !Array.isArray(cart.items)) {
    warnOnce(document, "liquid-ajax-cart cart state unavailable; leaving bundle progress widgets as-is");
    return;
  }

  document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
    try {
      updateWidget(root, cart);
    } catch (err) {
      // A single malformed widget must never break the others or bubble up.
      warnOnce(root, "unexpected error updating bundle progress widget", err);
    }
  });
}

document.addEventListener("liquid-ajax-cart:request-end", handleCartUpdate);
